import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Switch, TextField, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import * as T from '../types';
import { money, newGuid, parseNumber, unitShort } from '../labels';
import { ItemChoice, ItemPicker } from '../common';

/** "cart" a listed line (or fix its price), "replace" a listed line with something else, "unplanned" something not on the list. */
export type CartEntryMode = 'cart' | 'replace' | 'unplanned';

const priceText = (value: number) => value.toFixed(2).replace('.', ',');
const amountText = (value: number) => String(Math.round(value * 1000) / 1000).replace('.', ',');
const isWeighedUnit = (unit?: T.MeasureUnit | null) => unit === 'Kilogram' || unit === 'Gram';
const isCountedUnit = (unit?: T.MeasureUnit | null) => unit == null || unit === 'Unit' || unit === 'Package' || unit === 'Can';

const lineTotal = (entry: T.CartEntryRequest) =>
  Math.round((entry.lineTotal ?? (entry.weight != null ? entry.unitPrice! * entry.weight : entry.unitPrice! * (entry.quantity ?? 1))) * 100) / 100;

interface Fields { weighed: boolean; price: string; amount: string; labelTotal: string; promo: boolean; regular: string; }

function initialFields(mode: CartEntryMode, item: T.TripItem | null): Fields {
  const fields: Fields = { weighed: false, price: '', amount: '1', labelTotal: '', promo: false, regular: '' };
  if (mode !== 'cart' || !item) return fields;
  if (item.state === 'InCart') {
    // Fixing the price of a line already in the cart: start from what was entered
    fields.weighed = item.isWeighed;
    if (item.shelfUnitPrice != null) fields.price = priceText(item.shelfUnitPrice);
    else fields.labelTotal = priceText(item.lineTotal);
    const amount = item.isWeighed ? item.weight : item.cartQuantity;
    fields.amount = amount != null ? amountText(amount) : item.isWeighed ? '' : '1';
    fields.promo = item.isPromo;
    if (item.regularUnitPrice != null) fields.regular = priceText(item.regularUnitPrice);
    return fields;
  }
  fields.weighed = isWeighedUnit(item.unit);
  // 188 ml is one bottle: its price is the label's total, as the last price was recorded
  const measured = !fields.weighed && !isCountedUnit(item.unit) && item.plannedQuantity != null;
  if (item.lastShelfPrice != null) fields[measured ? 'labelTotal' : 'price'] = priceText(item.lastShelfPrice);
  if (item.plannedQuantity != null) {
    fields.amount = amountText(item.unit === 'Gram' ? item.plannedQuantity / 1000 : item.plannedQuantity);
  } else if (fields.weighed) {
    fields.amount = '';
  }
  return fields;
}

/** The entry, or an error to show. */
function buildEntry(f: Fields): { entry?: T.CartEntryRequest; error?: string } {
  let labelTotal: number | null = null;
  if (f.labelTotal.trim()) {
    labelTotal = parseNumber(f.labelTotal);
    if (labelTotal === null || labelTotal < 0) return { error: 'Valor da etiqueta inválido.' };
  }
  let price: number | null = null;
  if (f.price.trim()) {
    price = parseNumber(f.price);
    if (price === null || price < 0) return { error: 'Preço inválido.' };
  }
  if (price === null && labelTotal === null) return { error: 'Indica o preço.' };
  let amount: number | null = null;
  if (f.amount.trim()) {
    amount = parseNumber(f.amount);
    if (amount === null || amount <= 0) return { error: 'Quantidade inválida.' };
  }
  if (f.weighed && amount === null && labelTotal === null) return { error: 'Indica o peso ou o valor da etiqueta.' };
  const regular = f.promo ? parseNumber(f.regular) : null;
  return {
    entry: f.weighed
      ? { quantity: null, unitPrice: price, weight: amount, lineTotal: labelTotal, isPromo: f.promo, regularUnitPrice: regular }
      : { quantity: amount, unitPrice: price, weight: null, lineTotal: labelTotal, isPromo: f.promo, regularUnitPrice: regular },
  };
}

/**
 * Shelf price for something going into the cart. Builds the store-mode change and hands it to `onSubmit`,
 * which applies it and returns a note when the server did not apply it.
 */
const CartEntryDialog: React.FC<{
  open: boolean;
  mode: CartEntryMode;
  item: T.TripItem | null;
  onClose: () => void;
  onSubmit: (change: T.TripChangeRequest) => Promise<string | null>;
}> = ({ open, mode, item, onClose, onSubmit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [fields, setFields] = useState<Fields>(() => initialFields(mode, item));
  const [choice, setChoice] = useState<ItemChoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFields(initialFields(mode, item));
    setChoice(null);
    setError(null);
  }, [open, mode, item]);

  const set = (patch: Partial<Fields>) => setFields(f => ({ ...f, ...patch }));
  const choosesItem = mode !== 'cart';
  const built = buildEntry(fields);

  const chooseItem = (value: ItemChoice | null) => {
    setChoice(value);
    if (value && 'item' in value) set({ weighed: isWeighedUnit(value.item.defaultUnit) });
  };

  const save = async () => {
    if (!built.entry) { setError(built.error ?? null); return; }
    if (choosesItem && !choice) { setError('Escolhe ou cria o artigo.'); return; }
    const entry = built.entry;
    const itemRequest: T.TripItemRequest = {
      catalogItemPublicId: choice && 'item' in choice ? choice.item.publicId : null,
      newItemName: choice && 'newName' in choice ? choice.newName : null,
      newItemKind: choice && 'newName' in choice ? 'Food' : null,
      newItemSection: null,
      entry,
    };
    const change: T.TripChangeRequest = mode === 'replace'
      ? { clientChangeId: newGuid(), kind: 'Replace', itemPublicId: item!.publicId, item: itemRequest, newItemTempId: newGuid() }
      : mode === 'unplanned'
        ? { clientChangeId: newGuid(), kind: 'Unplanned', itemPublicId: null, item: itemRequest, newItemTempId: newGuid() }
        : { clientChangeId: newGuid(), kind: 'Cart', itemPublicId: item!.publicId, entry };
    setError(null);
    setSaving(true);
    try {
      const note = await onSubmit(change);
      if (note) setError(note);
      else onClose();
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'replace' ? `Em vez de ${item?.name ?? ''}` : mode === 'unplanned' ? 'Fora da lista' : item?.name ?? '';
  const hint = mode === 'replace'
    ? 'Escolhe o que compraste no lugar dele.'
    : mode === 'unplanned'
      ? 'Algo que não estava na lista. Se estiver por comprar noutra loja, sai de lá.'
      : item?.lastShelfPrice != null ? `Da última vez: ${money(item.lastShelfPrice)}` : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {hint && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{hint}</Typography>}
        <Stack spacing={2} sx={{ pt: hint ? 0 : 1 }}>
          {choosesItem && <ItemPicker value={choice} onChange={chooseItem} label="Artigo" autoFocus />}
          <FormControlLabel
            control={<Switch checked={fields.weighed} onChange={e => set({ weighed: e.target.checked })} />}
            label="Ao peso"
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              label={fields.weighed ? 'Preço por kg (€)' : 'Preço (€)'} value={fields.price} placeholder="0,00"
              onChange={e => set({ price: e.target.value })} autoFocus={!choosesItem}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }} fullWidth
            />
            <TextField
              label={fields.weighed ? 'Peso (kg)' : item?.unit && !isCountedUnit(item.unit) ? `Quantidade (${unitShort(item.unit)})` : 'Quantidade'}
              value={fields.amount}
              onChange={e => set({ amount: e.target.value })}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }} fullWidth
            />
          </Stack>
          <TextField
            label="Ou só o valor da etiqueta (€)" placeholder="Opcional" value={fields.labelTotal}
            onChange={e => set({ labelTotal: e.target.value })} slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />
          <FormControlLabel
            control={<Switch checked={fields.promo} onChange={e => set({ promo: e.target.checked })} />}
            label="Promoção"
          />
          {fields.promo && (
            <TextField
              label="Preço normal (opcional)" value={fields.regular} onChange={e => set({ regular: e.target.value })}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
          )}
          <Typography variant="h6">{built.entry ? `Total: ${money(lineTotal(built.entry))}` : ' '}</Typography>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Pôr no carrinho</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CartEntryDialog;
